import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MessageSquare, Trash2, Search, X, Folder, FolderOpen, FolderPlus, ChevronRight, ChevronDown, MoreHorizontal, Pencil } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { api } from '../lib/api';
import { toast } from 'sonner';

const ConvItem = ({ c, active, onSelect, onDelete, folders, onMove, onRename, snippet }) => {
  const [menu, setMenu] = useState(false);
  const [editing, setEditing] = useState(null);
  const { t } = useApp();
  const commit = () => { const v = (editing || '').trim(); if (v && v !== c.title) onRename(c.id, v); setEditing(null); };
  return (
    <div data-testid={`conv-item-${c.id}`} className={`group relative flex items-center gap-2 px-3 py-2 rounded-lg text-sm cursor-pointer transition-colors ${active ? 'bg-accent text-foreground' : 'text-muted-foreground hover:bg-accent/60 hover:text-foreground'}`} onClick={() => { if (editing === null) onSelect?.(c.id); }} onDoubleClick={(e) => { e.stopPropagation(); setEditing(c.title); }}>
      <MessageSquare className="h-4 w-4 shrink-0 opacity-60" />
      <span className="min-w-0 flex-1">
        {editing !== null ? (
          <input autoFocus data-testid={`conv-rename-input-${c.id}`} value={editing} onClick={(e) => e.stopPropagation()} onChange={(e) => setEditing(e.target.value)} onBlur={commit} onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setEditing(null); }} className="w-full h-6 rounded bg-background border border-primary/50 px-1.5 text-xs outline-none text-foreground" />
        ) : <span className="block truncate" data-testid={`conv-title-${c.id}`}>{c.title}</span>}
        {snippet && <span className="block truncate text-[11px] opacity-70" data-testid="search-snippet">{snippet}</span>}
      </span>
      <button data-testid={`conv-menu-${c.id}`} onClick={(e) => { e.stopPropagation(); setMenu((m) => !m); }} className="opacity-0 group-hover:opacity-100 hover:text-foreground transition-opacity"><MoreHorizontal className="h-3.5 w-3.5" /></button>
      <button data-testid={`conv-delete-${c.id}`} onClick={(e) => { e.stopPropagation(); onDelete?.(c.id); }} className="opacity-0 group-hover:opacity-100 hover:text-destructive transition-opacity"><Trash2 className="h-3.5 w-3.5" /></button>
      {menu && (
        <div className="absolute right-2 top-full mt-1 w-48 rounded-xl border border-border bg-popover shadow-lg p-1.5 z-30 animate-fade-in" onClick={(e) => e.stopPropagation()} data-testid="conv-move-menu">
          <button data-testid={`conv-rename-${c.id}`} onClick={() => { setMenu(false); setEditing(c.title); }} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-accent text-xs flex items-center gap-2"><Pencil className="h-3 w-3" /> {t.chat.rename}</button>
          <p className="px-2 py-1 mt-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{t.folders.moveTo}</p>
          {c.folder_id && <button onClick={() => { onMove(c.id, null); setMenu(false); }} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-accent text-xs">{t.folders.none}</button>}
          {folders.filter((f) => f.id !== c.folder_id).map((f) => (
            <button key={f.id} data-testid={`move-to-${f.id}`} onClick={() => { onMove(c.id, f.id); setMenu(false); }} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-accent text-xs flex items-center gap-2"><Folder className="h-3 w-3" /> {f.name}</button>
          ))}
          {folders.length === 0 && <p className="px-2 py-1.5 text-xs text-muted-foreground">{t.folders.empty}</p>}
        </div>
      )}
    </div>
  );
};

export const ConversationList = ({ conversations, activeConvId, onSelectConv, onDeleteConv, onConversationsChange }) => {
  const { t } = useApp();
  const [q, setQ] = useState('');
  const [hits, setHits] = useState(null);
  const [folders, setFolders] = useState([]);
  const [open, setOpen] = useState({});
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [renaming, setRenaming] = useState(null);
  const timer = useRef(null);

  useEffect(() => { api.listFolders().then(setFolders).catch(() => {}); }, []);

  useEffect(() => {
    clearTimeout(timer.current);
    if (q.trim().length < 2) { setHits(null); return; }
    timer.current = setTimeout(() => api.searchConversations(q.trim()).then(setHits).catch(() => setHits([])), 250);
    return () => clearTimeout(timer.current);
  }, [q]);

  const move = async (convId, folderId) => {
    try {
      await api.patchConversation(convId, { folder_id: folderId });
      onConversationsChange?.((prev) => prev.map((c) => (c.id === convId ? { ...c, folder_id: folderId } : c)));
      if (folderId) setOpen((o) => ({ ...o, [folderId]: true }));
      toast.success(t.folders.moved);
    } catch (e) { toast.error(e.message); }
  };

  const createFolder = async () => {
    if (!newName.trim()) { setCreating(false); return; }
    try { const f = await api.createFolder(newName.trim()); setFolders((p) => [...p, f]); setOpen((o) => ({ ...o, [f.id]: true })); toast.success(t.folders.created); } catch (e) { toast.error(e.message); }
    setNewName(''); setCreating(false);
  };

  const rename = async (f) => {
    if (!renaming?.name.trim()) { setRenaming(null); return; }
    try { const r = await api.renameFolder(f.id, renaming.name.trim()); setFolders((p) => p.map((x) => (x.id === f.id ? r : x))); } catch (e) { toast.error(e.message); }
    setRenaming(null);
  };

  const renameConv = async (convId, title) => {
    try {
      await api.patchConversation(convId, { title });
      onConversationsChange?.((prev) => prev.map((c) => (c.id === convId ? { ...c, title } : c)));
      setHits((h) => (h ? h.map((c) => (c.id === convId ? { ...c, title } : c)) : h));
      toast.success(t.chat.renamed);
    } catch (e) { toast.error(e.message); }
  };

  const removeFolder = async (f) => {
    try {
      await api.deleteFolder(f.id);
      setFolders((p) => p.filter((x) => x.id !== f.id));
      onConversationsChange?.((prev) => prev.map((c) => (c.folder_id === f.id ? { ...c, folder_id: null } : c)));
      toast.success(t.folders.deleted);
    } catch (e) { toast.error(e.message); }
  };

  const item = (c, snippet) => <ConvItem key={c.id} c={c} active={activeConvId === c.id} onSelect={onSelectConv} onDelete={onDeleteConv} folders={folders} onMove={move} onRename={renameConv} snippet={snippet} />;
  const { loose, byFolder } = useMemo(() => {
    const ids = new Set(folders.map((f) => f.id));
    const byFolder = {};
    const loose = [];
    for (const c of conversations) {
      if (c.folder_id && ids.has(c.folder_id)) (byFolder[c.folder_id] ||= []).push(c);
      else loose.push(c);
    }
    return { loose, byFolder };
  }, [conversations, folders]);

  return (
    <div className="mt-3 flex-1 overflow-y-auto px-3 min-h-0">
      <div className="relative mb-2">
        <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
        <input data-testid="conv-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t.chat.search} className="w-full h-8 rounded-lg bg-background border border-border pl-8 pr-7 text-xs outline-none focus:border-primary/50" />
        {q && <button onClick={() => setQ('')} data-testid="conv-search-clear" className="absolute right-2 top-2 text-muted-foreground hover:text-foreground"><X className="h-3.5 w-3.5" /></button>}
      </div>

      {hits !== null ? (
        <div className="space-y-0.5" data-testid="search-results">
          <p className="px-3 mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t.chat.results.replace('{n}', hits.length)}</p>
          {hits.map((c) => item(c, c.snippet))}
          {hits.length === 0 && <p className="px-3 py-2 text-xs text-muted-foreground" data-testid="search-empty">{t.chat.noResults}</p>}
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between px-3 mb-1">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t.folders.title}</p>
            <button onClick={() => setCreating(true)} data-testid="new-folder-btn" title={t.folders.new} className="text-muted-foreground hover:text-foreground"><FolderPlus className="h-3.5 w-3.5" /></button>
          </div>
          {creating && (
            <input autoFocus data-testid="new-folder-input" value={newName} onChange={(e) => setNewName(e.target.value)} onBlur={createFolder} onKeyDown={(e) => { if (e.key === 'Enter') createFolder(); if (e.key === 'Escape') { setCreating(false); setNewName(''); } }} placeholder={t.folders.namePh} className="w-full h-8 mb-1 rounded-lg bg-background border border-primary/50 px-3 text-xs outline-none" />
          )}
          <div className="space-y-0.5 mb-3">
            {folders.map((f) => {
              const inFolder = byFolder[f.id] || [];
              const isOpen = !!open[f.id];
              return (
                <div key={f.id} data-testid={`folder-${f.id}`}>
                  <div className="group flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-sm text-muted-foreground hover:bg-accent/60 hover:text-foreground cursor-pointer" onClick={() => setOpen((o) => ({ ...o, [f.id]: !isOpen }))}>
                    {isOpen ? <ChevronDown className="h-3.5 w-3.5 shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 shrink-0" />}
                    {isOpen ? <FolderOpen className="h-4 w-4 shrink-0 text-primary" /> : <Folder className="h-4 w-4 shrink-0" />}
                    {renaming?.id === f.id ? (
                      <input autoFocus value={renaming.name} onClick={(e) => e.stopPropagation()} onChange={(e) => setRenaming({ id: f.id, name: e.target.value })} onBlur={() => rename(f)} onKeyDown={(e) => { if (e.key === 'Enter') rename(f); if (e.key === 'Escape') setRenaming(null); }} className="flex-1 h-6 rounded bg-background border border-primary/50 px-1.5 text-xs outline-none" data-testid="rename-folder-input" />
                    ) : <span className="truncate flex-1 font-medium" data-testid="folder-name">{f.name}</span>}
                    <span className="text-[10px] opacity-60">{inFolder.length}</span>
                    <button onClick={(e) => { e.stopPropagation(); setRenaming({ id: f.id, name: f.name }); }} data-testid={`rename-folder-${f.id}`} className="opacity-0 group-hover:opacity-100 hover:text-foreground"><Pencil className="h-3 w-3" /></button>
                    <button onClick={(e) => { e.stopPropagation(); removeFolder(f); }} data-testid={`delete-folder-${f.id}`} className="opacity-0 group-hover:opacity-100 hover:text-destructive"><Trash2 className="h-3 w-3" /></button>
                  </div>
                  {isOpen && <div className="ml-4 pl-2 border-l border-border space-y-0.5">{inFolder.map((c) => item(c))}{inFolder.length === 0 && <p className="px-3 py-1.5 text-[11px] text-muted-foreground">{t.folders.emptyFolder}</p>}</div>}
                </div>
              );
            })}
          </div>
          {loose.length > 0 && <p className="px-3 mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t.chat.history}</p>}
          <div className="space-y-0.5">{loose.map((c) => item(c))}</div>
        </>
      )}
    </div>
  );
};
